/**
 * Ficha do toque numa área de chuva rastreada.
 *
 * O que se precisa saber em três segundos — qual área, de que hora, se está
 * crescendo, o tamanho, a chuva mais forte, para onde vai e há quanto tempo
 * existe — e, em "Mais detalhes" (no desktop, à vista), a evolução nas últimas
 * horas. Os números são os do GSMaP (mm/h, média da hora) e do pyForTraCC
 * (km², km/h). A ficha acompanha o player: no horário exibido, os números
 * daquela hora.
 */
import clsx from 'clsx';
import { ArrowUp, ChevronDown, Droplets, ExternalLink, Minus, Sparkles, TrendingDown, TrendingUp, type LucideIcon } from 'lucide-react';
import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { GSMAP_URL } from '../gsmap/api';
import { formatDecimal, formatInt } from '../lib/format';
import { formatTimeBrt } from '../lib/time';
import { useFormaDaFicha } from '../map/popupForma';
import { shownTimeOf, useTimeStore } from '../state/time';
import { PYFORTRACC_URL } from '../tracking/api';
import { duracao, nomeDaSituacao, pontoCardeal, siglaDoPonto } from '../tracking/TrackingPopup';
import { COR_CHUVA } from '../tracking/tracos';
import { useIsMobile } from '../ui/useMediaQuery';
import { EvolucaoDaChuva, serieDaChuva } from './Evolucao';
import { chuvaNoInstante } from './layer';
import { useRastreioChuvaStore, type SistemaChuva } from './store';

export type Tendencia = 'nova' | 'crescendo' | 'estavel' | 'diminuindo' | 'sem-dados';

/** Variação da área em 1 h que conta como crescer ou diminuir. */
export const LIMIAR_AREA_PCT = 15;

/**
 * A tendência pela área de 1 h atrás (dois horários): a chuva que se espalha
 * cresce, a que se desfaz diminui. Sem o ponto de 1 h atrás, o de 30 min.
 * Sem história: nova (se a situação diz) ou sem dados.
 */
export function tendenciaDaChuva(s: Pick<SistemaChuva, 'hist' | 'st' | 'km2'>): { classe: Tendencia; pct: number | null } {
  const h = s.hist;
  const agora = s.km2;
  if (!h || h.dt.length < 2 || agora === null || agora <= 0) return { classe: s.st?.includes('NEW') ? 'nova' : 'sem-dados', pct: null };
  let antes: number | null = null;
  for (const alvo of [-60, -30]) {
    const k = h.dt.indexOf(alvo);
    if (k >= 0 && h.km2[k] !== null && h.km2[k]! > 0) {
      antes = h.km2[k]!;
      break;
    }
  }
  if (antes === null) return { classe: 'sem-dados', pct: null };
  const pct = ((agora - antes) / antes) * 100;
  return { classe: pct >= LIMIAR_AREA_PCT ? 'crescendo' : pct <= -LIMIAR_AREA_PCT ? 'diminuindo' : 'estavel', pct };
}

const SELO: Readonly<Record<Tendencia, { nome: string; icone: LucideIcon; classe: string }>> = {
  nova: { nome: 'Nova', icone: Sparkles, classe: 'border-mata-400/50 bg-mata-400/15 text-mata-300' },
  crescendo: { nome: 'Crescendo', icone: TrendingUp, classe: 'border-red-400/50 bg-red-400/15 text-red-200' },
  estavel: { nome: 'Estável', icone: Minus, classe: 'border-ink-500/60 bg-ink-700/60 text-ink-100' },
  diminuindo: { nome: 'Diminuindo', icone: TrendingDown, classe: 'border-rio-400/50 bg-rio-400/15 text-rio-300' },
  'sem-dados': { nome: 'Sem tendência', icone: Minus, classe: 'border-ink-600 bg-ink-800 text-ink-300' },
};

function Selo(props: { t: ReturnType<typeof tendenciaDaChuva> }) {
  const { nome, icone: Icone, classe } = SELO[props.t.classe];
  const pct = props.t.pct;
  return (
    <span
      data-tendencia-chuva={props.t.classe}
      title={pct !== null ? `Área ${pct >= 0 ? '+' : '−'}${formatInt(Math.abs(pct))}% em relação a 1 h antes` : undefined}
      className={clsx('inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] leading-tight font-semibold', classe)}
    >
      <Icone className="size-3" aria-hidden />
      {nome}
    </span>
  );
}

export const mmh = (v: number) => `${formatDecimal(v, v < 10 ? 1 : 0)} mm/h`;
export const horaDaChuva = (s: Pick<SistemaChuva, 'ini' | 'fim'>) => `${formatTimeBrt(s.ini)}–${formatTimeBrt(s.fim)}`;
export const tituloDaChuva = (s: Pick<SistemaChuva, 'uid'>) => (s.uid !== null ? `Chuva ${s.uid}` : 'Área de chuva');

function Chip(props: { rotulo: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 items-baseline gap-1 text-xs leading-snug whitespace-nowrap">
      <dt className="text-[11px] text-ink-400">{props.rotulo}</dt>
      <dd className="font-medium text-ink-100 tabular-nums">{props.children}</dd>
    </div>
  );
}

function Dado(props: { rotulo: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="truncate text-[10px] leading-tight text-ink-400">{props.rotulo}</dt>
      <dd className="truncate text-xs leading-snug font-medium text-ink-100 tabular-nums">{props.children}</dd>
    </div>
  );
}

function Rumo(props: { s: SistemaChuva; curto?: boolean }) {
  const { vel, rumo } = props.s;
  if (vel === null) return null;
  const anda = rumo !== null && vel > 0;
  return (
    <span className="inline-flex items-baseline gap-1">
      {anda && <ArrowUp className="size-3.5 shrink-0 self-center" strokeWidth={2.5} style={{ transform: `rotate(${rumo}deg)`, color: COR_CHUVA }} aria-hidden />}
      {formatInt(vel)} km/h
      {anda &&
        (props.curto ? (
          <abbr title={`para ${pontoCardeal(rumo)}`} aria-label={`para ${pontoCardeal(rumo)}`} className="font-normal text-ink-300 no-underline">
            {siglaDoPonto(rumo)}
          </abbr>
        ) : (
          <span className="font-normal text-ink-300">{pontoCardeal(rumo)}</span>
        ))}
    </span>
  );
}

function Expansao(props: { v: number }) {
  return (
    <>
      {props.v > 0 ? '+' : props.v < 0 ? '−' : ''}
      {formatInt(Math.abs(props.v))}
      <span className="font-normal text-ink-400"> ×10⁻⁶/s</span>
    </>
  );
}

/** "Mais detalhes" da ficha compacta: começa fechado a cada área; aberto, a ficha pode cobri-la. */
function Detalhes(props: { children: ReactNode }) {
  const id = useId();
  const [abertos, setAbertos] = useState(false);
  const { aoAbrirDetalhes } = useFormaDaFicha();
  useEffect(() => {
    aoAbrirDetalhes(abertos);
    return () => aoAbrirDetalhes(false);
  }, [abertos, aoAbrirDetalhes]);
  return (
    <div className="border-t border-white/10">
      <button
        type="button"
        aria-expanded={abertos}
        aria-controls={id}
        onClick={() => setAbertos(!abertos)}
        className="flex min-h-9 w-full items-center gap-1 rounded-md text-left text-xs font-medium text-mata-300 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mata-400 pointer-coarse:min-h-11"
      >
        <span className="flex-1">{abertos ? 'Menos detalhes' : 'Mais detalhes'}</span>
        <ChevronDown className={clsx('size-4 transition-transform', abertos && 'rotate-180')} aria-hidden />
      </button>
      <div id={id} hidden={!abertos} className="space-y-1.5 pb-0.5">
        {abertos && props.children}
      </div>
    </div>
  );
}

function Fonte() {
  return (
    <div className="flex flex-wrap gap-x-2 text-[11px] text-ink-400">
      <a href={GSMAP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-rio-300 hover:underline">
        GSMaP (JAXA)
        <ExternalLink size={10} />
      </a>
      <a href={PYFORTRACC_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-rio-300 hover:underline">
        pyForTraCC
        <ExternalLink size={10} />
      </a>
    </div>
  );
}

export function FichaDaChuva(props: { sistema: SistemaChuva }) {
  const clicado = props.sistema;
  const celular = useIsMobile();
  const { forma } = useFormaDaFicha();
  const compacta = celular || forma === 'compacta';
  const instante = useTimeStore((st) => shownTimeOf(st));
  // A lista do quadro na tela muda quando um arquivo chega: é a deixa para procurar a área de novo.
  const naTela = useRastreioChuvaStore((st) => st.sistemas);
  const uid = clicado.uid;
  const noQuadro = useMemo(() => {
    void naTela;
    if (uid === null) return null;
    return chuvaNoInstante(uid, instante) ?? (instante !== null && instante >= clicado.ini && instante <= clicado.fim ? clicado : null);
  }, [uid, instante, naTela, clicado]);
  // A ficha aberta é a seleção: a área fica destacada no mapa e na lista.
  useEffect(() => {
    useRastreioChuvaStore.getState().setSelecao(uid);
    return () => {
      if (useRastreioChuvaStore.getState().selecao === uid) useRastreioChuvaStore.getState().setSelecao(null);
    };
  }, [uid]);
  const s = noQuadro ?? clicado;
  const t = useMemo(() => tendenciaDaChuva(s), [s]);
  const serie = useMemo(() => serieDaChuva(s.hist, s.t), [s]);
  const ausente = noQuadro === null && instante !== null;
  const situacao = nomeDaSituacao(s.st);
  const aviso = ausente && (
    <div className="text-[11px] leading-snug text-yellow-200" data-ficha-ausente="">
      Sem esta área na imagem das {formatTimeBrt(instante)}: os números são os da hora {horaDaChuva(s)}.
    </div>
  );
  const grafico = serie ? <EvolucaoDaChuva serie={serie} instante={instante} /> : <div className="text-[11px] text-ink-400">Área nova: ainda sem história.</div>;

  if (compacta) {
    return (
      <div className="w-[min(16rem,72vw)] space-y-1" data-ficha-chuva="" data-ficha-compacta="">
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 pr-5 leading-tight">
          <Droplets className="size-4 shrink-0" style={{ color: COR_CHUVA }} aria-hidden />
          <span className="font-semibold text-white">{tituloDaChuva(s)}</span>
          <span className="text-[11px] text-ink-300 tabular-nums">{horaDaChuva(s)}</span>
          <Selo t={t} />
        </div>
        {aviso}
        <dl className="grid grid-cols-2 gap-x-3 gap-y-0.5">
          {s.km2 !== null && <Chip rotulo="Área">{formatInt(s.km2)} km²</Chip>}
          {s.pmax !== null && <Chip rotulo="Máx.">{mmh(s.pmax)}</Chip>}
          {s.vel !== null && (
            <Chip rotulo="Vai">
              <Rumo s={s} curto />
            </Chip>
          )}
          {s.vida !== null && <Chip rotulo="Vida">{duracao(s.vida)}</Chip>}
        </dl>
        <Detalhes key={uid ?? 'x'}>
          <dl className="grid grid-cols-2 gap-x-3 gap-y-0.5">
            {s.pmed !== null && <Chip rotulo="Média">{mmh(s.pmed)}</Chip>}
            {s.dp !== null && <Chip rotulo="Desvio">{mmh(s.dp)}</Chip>}
            {s.exp !== null && (
              <Chip rotulo="Expansão">
                <Expansao v={s.exp} />
              </Chip>
            )}
            {situacao && <Chip rotulo="Situação">{situacao}</Chip>}
          </dl>
          <div className="text-ink-200">{grafico}</div>
          <Fonte />
        </Detalhes>
      </div>
    );
  }
  return (
    <div className="w-[min(18rem,74vw)] space-y-1.5" data-ficha-chuva="">
      <div className="pr-5 text-xs text-ink-400">Área de chuva · GSMaP e pyForTraCC</div>
      <div className="flex items-start gap-2 pr-5">
        <Droplets className="mt-0.5 size-4 shrink-0" style={{ color: COR_CHUVA }} aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="leading-tight font-semibold text-white">{tituloDaChuva(s)}</div>
          <div className="text-[11px] leading-snug text-ink-300 tabular-nums">
            {[`hora ${horaDaChuva(s)}`, situacao, s.vida !== null ? `${duracao(s.vida)} de vida` : null].filter(Boolean).join(' · ')}
          </div>
        </div>
        <Selo t={t} />
      </div>
      {aviso}
      <dl className="grid grid-cols-3 gap-x-3 gap-y-1.5 border-t border-white/10 pt-1.5">
        {s.km2 !== null && <Dado rotulo="Área ≥ 0,1 mm/h">{formatInt(s.km2)} km²</Dado>}
        {s.pmax !== null && <Dado rotulo="Mais forte">{mmh(s.pmax)}</Dado>}
        {s.pmed !== null && <Dado rotulo="Média">{mmh(s.pmed)}</Dado>}
        {s.vel !== null && (
          <Dado rotulo="Deslocamento">
            <Rumo s={s} curto />
          </Dado>
        )}
        {s.exp !== null && (
          <Dado rotulo="Expansão">
            <Expansao v={s.exp} />
          </Dado>
        )}
        {s.dp !== null && <Dado rotulo="Desvio-padrão">{mmh(s.dp)}</Dado>}
      </dl>
      <div className="border-t border-white/10 pt-0.5 text-ink-200">{grafico}</div>
      <div className="border-t border-white/10 pt-1">
        <Fonte />
      </div>
    </div>
  );
}
